export const AUDIT_SOUND_KEY = 'cv_audit_completion_sound';

export function readAuditSoundPreference(storage) {
  try {
    return (storage === undefined ? globalThis.localStorage : storage)?.getItem(AUDIT_SOUND_KEY) === 'true';
  } catch {
    return false;
  }
}

export class AuditCompletionSound {
  constructor(getAudioContext = () => globalThis.AudioContext || globalThis.webkitAudioContext) {
    this.getAudioContext = getAudioContext;
    this.enabled = false;
    this.context = null;
    this.generation = 0;
    this.previewGeneration = 0;
    this.disposed = false;
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) {
      this.previewGeneration++;
      this.cancel();
      this.close();
    }
    // Called inside the bell switch's click gesture. A preview is independent
    // of the active analysis token and must never consume its completion.
    if (enabled) this.unlock(true);
  }

  unlock(preview = false) {
    if (!this.enabled || this.disposed) return;
    const previewToken = preview ? ++this.previewGeneration : null;
    try {
      if (!this.context) {
        const Context = this.getAudioContext();
        if (!Context) return;
        this.context = new Context();
      }
      if (this.context.state === 'suspended') {
        Promise.resolve(this.context.resume())
          .then(() => { if (preview && previewToken === this.previewGeneration && this.enabled && !this.disposed) this.playTone(true); })
          .catch((error) => this.reportAudioError(error));
      } else if (preview) {
        this.playTone(true);
      }
    } catch (error) {
      this.reportAudioError(error);
    }
  }

  reportAudioError(error) {
    // Browsers may silently refuse audio until another gesture. Device or
    // scheduling errors are different and should remain diagnosable.
    if (error?.name !== 'NotAllowedError' && error?.name !== 'SecurityError') {
      console.warn('Audit sound unavailable:', error);
    }
  }

  start(userInitiated = false) {
    this.cancel();
    if (!userInitiated || !this.enabled || this.disposed) return null;
    // Do not defer this call: browsers require resume() within the start gesture.
    this.unlock();
    return this.generation;
  }

  complete(token) {
    if (token === null || token === undefined || !this.enabled || this.disposed || token !== this.generation) return;
    this.generation++; // consume exactly once, including when audio is unavailable
    this.playTone(false);
  }

  playTone(preview) {
    const context = this.context;
    if (!context || context.state !== 'running') return;
    const nodes = [];
    try {
      const now = context.currentTime;
      for (const [frequency, offset] of (preview ? [[659.25, 0]] : [[523.25, 0], [659.25, 0.16]])) {
        const oscillator = context.createOscillator();
        nodes.push(oscillator);
        const gain = context.createGain();
        nodes.push(gain);
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequency, now + offset);
        gain.gain.setValueAtTime(0, now + offset);
        gain.gain.linearRampToValueAtTime(0.055, now + offset + 0.025);
        gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.28);
        oscillator.connect(gain);
        gain.connect(context.destination);
        oscillator.onended = () => {
          oscillator.disconnect();
          gain.disconnect();
        };
        oscillator.start(now + offset);
        oscillator.stop(now + offset + 0.29);
      }
    } catch (error) {
      // Scheduling errors never affect results or leave an open connection.
      for (const node of nodes) {
        try { node.disconnect(); } catch {}
      }
      this.reportAudioError(error);
    }
  }

  cancel(token) {
    if (token !== undefined && token !== null && token !== this.generation) return;
    this.generation++;
  }

  dispose() {
    this.disposed = true;
    this.previewGeneration++;
    this.cancel();
    this.close();
  }

  close() {
    if (this.context) {
      const context = this.context;
      this.context = null;
      try {
        Promise.resolve(context.close()).catch(() => {});
      } catch {
        // Closing an unavailable audio device is best effort.
      }
    }
  }
}